import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, ChevronLeft, ChevronRight, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { scrollToId } from "@/lib/scroll";
import api from "@/lib/api";
import AmbientGlow from "@/components/AmbientGlow";
import { SharedDefs } from "@/components/workspaces/WorkspaceVisuals";
import { fileUrl, VISUAL_MAP } from "@/lib/galleryVisuals";

const EASE = [0.22, 1, 0.36, 1];

const TABS = ["All", "Coworking", "Private Cabins", "Premium Cabins", "Meeting Room", "Facilities"];

const FALLBACK_ITEMS = [
    { id: "coworking-floor", title: "The Coworking Floor", category: "Coworking", visual_key: "coworking", image_path: null, span: "lg:col-span-7 lg:row-span-2", h: "h-64 sm:h-72 lg:h-full", featured: true },
    { id: "private-cabin", title: "Private Cabin", category: "Private Cabins", visual_key: "private", image_path: null, span: "lg:col-span-5", h: "h-56 lg:h-[202px]" },
    { id: "meeting-room", title: "The Meeting Room", category: "Meeting Room", visual_key: "meeting", image_path: null, span: "lg:col-span-5", h: "h-56 lg:h-[202px]" },
    { id: "premium-cabin", title: "Premium Cabin", category: "Premium Cabins", visual_key: "premium", image_path: null, span: "lg:col-span-5", h: "h-56 lg:h-[232px]" },
    { id: "coffee-breakout", title: "Coffee & Breakout", category: "Facilities", visual_key: "coffee", image_path: null, span: "lg:col-span-7", h: "h-56 lg:h-[232px]" },
    { id: "focus-zone", title: "Focus Zone", category: "Coworking", visual_key: "solo", image_path: null, span: "lg:col-span-6", h: "h-52 lg:h-[220px]" },
    { id: "reception", title: "Reception & Common Area", category: "Facilities", visual_key: "reception", image_path: null, span: "lg:col-span-6", h: "h-52 lg:h-[220px]" },
];

export default function GallerySection() {
    const [tab, setTab] = useState("All");
    const [open, setOpen] = useState(null);
    const [allItems, setAllItems] = useState(FALLBACK_ITEMS);


const GalleryMedia = ({ item }) => {
    if (item.image_path) {
        return <img src={fileUrl(item.image_path)} alt={item.title} loading="lazy" className="h-full w-full object-cover" />;
    }
    const Visual = VISUAL_MAP[item.visual_key];
    return Visual ? <Visual /> : null;
};

    useEffect(() => {
        api.get("/api/gallery")
            .then((res) => {
                if (Array.isArray(res.data?.items) && res.data.items.length > 0) {
                    setAllItems(res.data.items);
                }
            })
            .catch(() => {});
    }, []);

    const items = useMemo(
        () => (tab === "All" ? allItems : allItems.filter((it) => it.category === tab)),
        [tab, allItems],
    );

    useEffect(() => {
        if (open === null) return;
        const onKey = (e) => {
            if (e.key === "Escape") setOpen(null);
            if (e.key === "ArrowRight") setOpen((o) => (o + 1) % items.length);
            if (e.key === "ArrowLeft") setOpen((o) => (o - 1 + items.length) % items.length);
        };
        window.addEventListener("keydown", onKey);
        window.__lenis?.stop();
        return () => {
            window.removeEventListener("keydown", onKey);
            window.__lenis?.start();
        };
    }, [open, items.length]);

    const ActiveItem = open !== null ? items[open] : null;

    return (
        <section id="gallery" data-testid="gallery-section" className="cv-auto relative overflow-hidden py-20 sm:py-28">
            <SharedDefs />
            <AmbientGlow color="#7E35A8" side="right" className="-right-40 top-24 h-[440px] w-[440px]" />

            <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
                {/* Intro */}
                <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 0.7, ease: EASE }}
                    >
                        <p
                            data-testid="gallery-eyebrow"
                            className="inline-flex items-center gap-2.5 rounded-full border border-brand-purple/15 bg-white/70 px-4 py-2 text-[11px] font-bold uppercase tracking-[0.24em] text-ink/60"
                        >
                            <span className="h-1.5 w-1.5 rounded-full bg-gradient-to-r from-brand-purple to-brand-teal" />
                            Explore the Space
                        </p>
                        <h2 className="mt-6 text-4xl font-bold leading-[1.08] tracking-tight text-ink sm:text-5xl">
                            Take a <span className="text-gradient">Look Around.</span>
                        </h2>
                    </motion.div>
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 0.7, ease: EASE, delay: 0.1 }}
                        className="max-w-md text-sm font-medium leading-relaxed text-ink/55 md:text-base"
                    >
                        Explore the spaces designed to help you focus, collaborate and work comfortably.
                    </motion.p>
                </div>

                {/* Filter tabs */}
                <motion.div
                    initial={{ opacity: 0, y: 14 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-60px" }}
                    transition={{ duration: 0.6, ease: EASE, delay: 0.12 }}
                    className="mt-10 flex flex-wrap items-center gap-2"
                    role="tablist"
                    aria-label="Gallery categories"
                >
                    {TABS.map((t) => (
                        <button
                            key={t}
                            role="tab"
                            aria-selected={tab === t}
                            data-testid={`gallery-tab-${t.toLowerCase().replace(/\s+/g, "-")}`}
                            onClick={() => setTab(t)}
                            className={`rounded-full border px-4 py-2 text-[12.5px] font-bold transition-all duration-300 ${
                                tab === t
                                    ? "border-transparent bg-gradient-to-r from-brand-purple/10 via-brand-blue/10 to-brand-teal/10 text-transparent [background-clip:padding-box] shadow-[inset_0_0_0_1.5px_rgba(43,92,171,0.35)]"
                                    : "border-slate-200 bg-white text-ink/55 hover:border-ink/25 hover:text-ink"
                            }`}
                        >
                            <span className={tab === t ? "text-gradient" : ""}>{t}</span>
                        </button>
                    ))}
                </motion.div>

                {/* Editorial grid */}
                <motion.div layout className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-12">
                    <AnimatePresence mode="popLayout">
                        {items.map((item, i) => (
                            <motion.button
                                layout
                                key={item.id}
                                initial={{ clipPath: "inset(0 0 100% 0)", opacity: 0.3 }}
                                whileInView={{ clipPath: "inset(0 0 0% 0)", opacity: 1 }}
                                exit={{ opacity: 0, scale: 0.97 }}
                                viewport={{ once: true, margin: "-50px" }}
                                transition={{ duration: 0.7, ease: EASE, delay: item.featured ? 0 : i * 0.07 }}
                                onClick={() => setOpen(i)}
                                data-testid={`gallery-item-${item.id}`}
                                className={`group relative overflow-hidden rounded-3xl border border-slate-200/80 bg-gradient-to-br from-[#F6F7FB] to-[#EDF0F7] text-left transition-shadow duration-500 hover:shadow-[0_30px_65px_-35px_rgba(22,50,79,0.4)] sm:col-span-1 ${item.span}`}
                            >
                                <div aria-hidden="true" className="blueprint-grid absolute inset-0 opacity-60" />
                                <div className={`relative ${item.h} transition-transform duration-700 ease-out group-hover:scale-[1.03] [&_svg]:!h-full`}>
                                    <GalleryMedia item={item} />
                                </div>

                                {/* hover overlay */}
                                <div className="pointer-events-none absolute inset-0 flex flex-col justify-between p-5 opacity-0 transition-opacity duration-500 group-hover:opacity-100">
                                    <div className="flex justify-end">
                                        <span className="flex h-9 w-9 items-center justify-center rounded-full bg-white/90 text-ink shadow-md transition-transform duration-500 group-hover:rotate-0">
                                            <ArrowUpRight size={16} strokeWidth={2.4} />
                                        </span>
                                    </div>
                                    <div className="rounded-2xl bg-gradient-to-t from-ink/70 via-ink/30 to-transparent p-4 pt-10">
                                        <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/70">
                                            {item.category}
                                        </p>
                                        <p className="mt-1 text-lg font-bold tracking-tight text-white">
                                            {item.title}
                                        </p>
                                    </div>
                                </div>

                                <span
                                    aria-hidden="true"
                                    className="absolute inset-x-0 bottom-0 h-[3px] w-0 bg-gradient-to-r from-brand-purple via-brand-blue to-brand-teal transition-all duration-700 group-hover:w-full"
                                />
                                {!item.image_path && (
                                    <p className="pointer-events-none absolute bottom-3 right-4 text-[9px] font-semibold tracking-wide text-ink/30 transition-opacity duration-300 group-hover:opacity-0">
                                        Conceptual visualization
                                    </p>
                                )}
                            </motion.button>
                        ))}
                    </AnimatePresence>
                </motion.div>

                {/* CTA */}
                <motion.div
                    initial={{ opacity: 0, y: 18 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-60px" }}
                    transition={{ duration: 0.7, ease: EASE }}
                    className="mt-16 flex flex-col items-center gap-5 text-center"
                >
                    <p className="text-lg font-bold tracking-tight text-ink md:text-xl">
                        Want to experience it in person?
                    </p>
                    <button
                        data-testid="gallery-book-demo-btn"
                        onClick={() => scrollToId("#book-demo")}
                        className="btn-gradient group inline-flex items-center gap-2.5 rounded-full px-7 py-4 text-[15px] font-bold text-white"
                    >
                        Book a Free Demo
                        <ArrowRight size={17} strokeWidth={2.6} className="transition-transform duration-300 group-hover:translate-x-1" />
                    </button>
                </motion.div>
            </div>

            {/* Lightbox */}
            <AnimatePresence>
                {ActiveItem && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        data-testid="gallery-lightbox"
                        role="dialog"
                        aria-modal="true"
                        aria-label={ActiveItem.title}
                        className="fixed inset-0 z-[80] flex items-center justify-center bg-ink/60 p-4 backdrop-blur-md"
                        onClick={() => setOpen(null)}
                    >
                        <motion.div
                            initial={{ scale: 0.96, y: 24, opacity: 0 }}
                            animate={{ scale: 1, y: 0, opacity: 1 }}
                            exit={{ scale: 0.97, y: 16, opacity: 0 }}
                            transition={{ duration: 0.45, ease: EASE }}
                            onClick={(e) => e.stopPropagation()}
                            className="relative w-full max-w-4xl overflow-hidden rounded-[2rem] border border-slate-200/60 bg-white shadow-2xl"
                        >
                            <div className="flex items-center justify-between px-6 py-4">
                                <div>
                                    <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-gradient">
                                        {ActiveItem.category}
                                    </p>
                                    <p className="mt-0.5 text-lg font-bold tracking-tight text-ink">
                                        {ActiveItem.title}
                                    </p>
                                </div>
                                <button
                                    data-testid="gallery-lightbox-close"
                                    onClick={() => setOpen(null)}
                                    aria-label="Close gallery"
                                    className="flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 text-ink/60 transition-colors hover:text-ink"
                                >
                                    <X size={17} />
                                </button>
                            </div>
                            <div className="relative h-[46vh] bg-gradient-to-br from-[#F6F7FB] to-[#EDF0F7] sm:h-[54vh]">
                                <div aria-hidden="true" className="blueprint-grid absolute inset-0 opacity-60" />
                                <div className="relative h-full [&_svg]:!h-full">
                                    <GalleryMedia item={ActiveItem} />
                                </div>
                                {!ActiveItem.image_path && (
                                    <p className="pointer-events-none absolute bottom-3 right-4 text-[9px] font-semibold tracking-wide text-ink/30">
                                        Conceptual visualization
                                    </p>
                                )}
                            </div>
                            <div className="flex items-center justify-between border-t border-slate-100 px-6 py-4">
                                <p className="text-xs font-bold text-ink/40">
                                    {open + 1} / {items.length}
                                </p>
                                <div className="flex items-center gap-2.5">
                                    <button
                                        data-testid="gallery-lightbox-prev"
                                        onClick={() => setOpen((o) => (o - 1 + items.length) % items.length)}
                                        aria-label="Previous image"
                                        className="flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 text-ink/60 transition-all hover:border-ink/30 hover:text-ink active:scale-95"
                                    >
                                        <ChevronLeft size={17} />
                                    </button>
                                    <button
                                        data-testid="gallery-lightbox-next"
                                        onClick={() => setOpen((o) => (o + 1) % items.length)}
                                        aria-label="Next image"
                                        className="flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 text-ink/60 transition-all hover:border-ink/30 hover:text-ink active:scale-95"
                                    >
                                        <ChevronRight size={17} />
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </section>
    );
}
